import * as React from "react";
import {FormattedPageTitle} from "@/components/pages";
import DeleteGatewayForm from "@/components/gateway/delete/DeleteGatewayForm";
import {FormActionButtons, FormattedFormRow, FormContainer} from "@dvrt/graphql-widgets";
import {Typography} from "@mui/material";
import {defineMessage, FormattedMessage} from "react-intl";
import Section from "@/components/Section";
import Loading from "@/components/breadcrumbs/Loading";
import TargetSelector, {Target, TargetType} from "@/components/TargetSelector";
import {Link} from "react-router";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ReferenceType} from "@/components/gateway/delete/DeleteGatewayForm";
import {useGatewayRelatedDataQuery} from "../queries.generated";
import {useGetTargetsQuery} from "@/components/fax/queries.generated";
import {msg_delete} from "@/utils/translations";

const DeleteGatewayPage = ({domainId, gatewayId}: {domainId: number; gatewayId: number}) => {
    const {loading, data} = useGatewayRelatedDataQuery({
        variables: {gatewayId},
    });

    const targetsData = useGetTargetsQuery({
        variables: {domainId},
    });

    const form = new DeleteGatewayForm(domainId, gatewayId);

    const exclamationIcon = () => <FontAwesomeIcon style={{color: "red", marginRight: "10px"}} icon="exclamation" />;

    if (loading || targetsData.loading) return <Loading />;

    function getTarget(): Target[] {
        const extensionTargets: Target[] = targetsData.data.gateways.items
            .filter((gateway) => gateway.id !== gatewayId)
            .map((gw) => ({
                numberId: gw.number.id,
                displayName: gw.displayName,
                shortNumber: gw.number.shortNumber,
                type: TargetType.GW,
            }));

        return extensionTargets.concat(
            targetsData.data.faxes.items.map(
                (fax): Target => ({
                    numberId: fax.number.id,
                    displayName: null,
                    shortNumber: fax.number.shortNumber,
                    type: TargetType.FAX,
                }),
            ),
        );
    }

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle desc={defineMessage({id: "delete", defaultMessage: "Delete"})} />
            <Section
                label={defineMessage({
                    id: "gateway_inconsistency.please_solve",
                    defaultMessage: "Please solve the following inconsistencies",
                })}
                hidden={!data.relatedData.overrideCaller.length}
            >
                <FormContainer>
                    {data.relatedData.overrideCaller.map((extension, index) => (
                        <React.Fragment key={index}>
                            <Typography>
                                {exclamationIcon()}
                                <FormattedMessage
                                    id="gateway_inconsistency.select_extension"
                                    defaultMessage="Select new alternate caller number for the user {name}"
                                    values={{
                                        name: (
                                            <Link to={`/domains/${domainId}/users/${extension.id}/generic`}>
                                                {extension.displayName}
                                            </Link>
                                        ),
                                    }}
                                />
                            </Typography>
                            <TargetSelector
                                handleChange={(id) => {
                                    form.handleChange(id, extension.id, ReferenceType.USER);
                                }}
                                variant="standard"
                                selected={form.selections[extension.id]}
                                targets={getTarget()}
                                style={TargetSelectorStyle}
                                notSelectedText={defineMessage({id: "clear", defaultMessage: "Clear"})}
                            />
                        </React.Fragment>
                    ))}
                </FormContainer>
            </Section>
            <FormattedFormRow>
                <div style={{margin: "1em 0"}}>
                    <FormActionButtons
                        mainButtonText={msg_delete}
                        mainAction={() => {
                            form.delete();
                        }}
                        mainButtonColor="secondary"
                        secondaryAction={() => {
                            form.reset();
                        }}
                        secondaryButtonHidden={!data.relatedData.overrideCaller.length}
                    />
                </div>
            </FormattedFormRow>
        </div>
    );
};

export default DeleteGatewayPage;

const TargetSelectorStyle = {
    marginLeft: "25px",
    marginRight: "25px",
    width: "50%",
} as React.CSSProperties;
